import { useState } from "react";
import { useAuth } from "../lib/auth";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  // Only accept relative same-origin paths in ``?next=`` so an attacker
  // can't turn our login into an open-redirect gadget.
  const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
    ? nextParam
    : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") return <Navigate to={safeNext} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate(safeNext);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative bg-slate-950 text-white">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img
              src="/vyntric-logo.png"
              alt="Vyntric"
              className="w-11 h-11 object-contain"
              data-testid="login-logo"
            />
            <div>
              <div className="text-sm font-black tracking-tight">Vyntric</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Multimarketplace
              </div>
            </div>
          </div>
          <div>
            <h1 className="text-5xl font-black tracking-tighter leading-[0.95]">
              Centralize<br />pedidos.<br />Acelere a<br />expedição.
            </h1>
            <p className="mt-6 max-w-md text-sm text-slate-300 leading-relaxed">
              Vyntric sincroniza Mercado Livre, Shopee, Amazon, Magalu, TikTok Shop e
              Shein — com Tiny ERP, controle de notas, etiquetas e guias de separação em um único
              fluxo.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                ["+1.000", "pedidos/dia"],
                ["6", "marketplaces"],
                ["15", "módulos"],
              ].map(([k, v]) => (
                <div key={v} className="border-l-2 border-[#002EA6] pl-3">
                  <div className="text-2xl font-black tracking-tight">{k}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            v1.0 · Control Room Edition
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            Acesso restrito
          </div>
          <h2 className="text-3xl font-black tracking-tighter mb-1">Entrar no Vyntric</h2>
          <p className="text-sm text-slate-600 mb-8">
            Use suas credenciais corporativas para continuar.
          </p>

          <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700">
            E-mail
          </label>
          <input
            data-testid="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002EA6] focus:ring-1 focus:ring-[#002EA6]"
            placeholder="seu@email.com"
          />

          <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mt-4">
            Senha
          </label>
          <input
            data-testid="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002EA6] focus:ring-1 focus:ring-[#002EA6]"
            placeholder="••••••••"
          />

          {error && (
            <div
              data-testid="login-error"
              className="mt-4 px-3 py-2 text-xs bg-red-50 text-red-800 border border-red-200"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-[#002EA6] hover:bg-[#00207A] text-white font-bold text-sm tracking-wide py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Entrar
          </button>
          <div className="mt-3 text-right">
            <a
              href="/forgot-password"
              data-testid="login-forgot-link"
              className="text-xs text-slate-500 hover:text-[#002EA6] hover:underline"
            >
              Esqueci minha senha
            </a>
          </div>
          <div className="mt-4 text-center text-xs text-slate-500">
            Não tem conta ainda?{" "}
            <a href="/signup" data-testid="login-signup-link" className="text-[#002EA6] font-bold hover:underline">
              Comece 7 dias grátis
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
